import type {
    ApiResponse,
    Nullable,
} from "@internal/backstage-plugin-utility-common";

import type {
    CreatePullRequestBody,
    CreatePullRequestResult,
    RepoContentEntry,
    RepoTreeEntry,
} from "../../types";
import {
    GitHubBaseClient,
    type GitHubClientRequestOptions,
    type GitHubGetTreeOptions,
} from "../GitHubBaseClient";

/**
 * Sub-client for GitHub repository content and pull-request endpoints.
 *
 * Mounted automatically onto {@link GitHubClient} at the `repo` property
 * by `ClientRegistry` when `github-common` is evaluated.
 *
 * @public
 */
export class GitHubRepoClient extends GitHubBaseClient {
    /**
     * Creates a GitHub pull request with the provided file changes.
     *
     * Returns `null` in the response data when no file changes are detected.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param body - Pull request body including title, head, changes, etc.
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with pull request data or null if no changes
     */
    createPullRequest(
        owner: string,
        repo: string,
        body: CreatePullRequestBody,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<Nullable<CreatePullRequestResult>>> {
        return this.api<Nullable<CreatePullRequestResult>>(
            `repo/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
                repo
            )}/pulls`,
            {
                method: "POST",
                headers: this.buildGitHubHeaders(options),
                body: JSON.stringify(body),
            }
        );
    }

    /**
     * Fetches the SHA of the latest commit that touched a specific file.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param filePath - The file path relative to the repository root
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with the full commit SHA string
     */
    fetchLatestCommitForFile(
        owner: string,
        repo: string,
        filePath: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<string>> {
        return this.api<string>(
            `repo/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
                repo
            )}/commits/latest-for-file?filePath=${encodeURIComponent(
                filePath
            )}`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Fetches the latest release tag name for a repository.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with the tag name string (e.g., "v1.2.3")
     */
    fetchLatestRelease(
        owner: string,
        repo: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<string>> {
        return this.api<string>(
            `repo/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
                repo
            )}/releases/latest`,
            {
                method: "GET",
                headers: this.buildGitHubHeaders(options),
            }
        );
    }

    /**
     * Fetches the contents of a directory in a repository.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param path - The directory path relative to the repository root
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with an array of content entries (files and directories)
     */
    getContents(
        owner: string,
        repo: string,
        path: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<RepoContentEntry[]>> {
        return this.api<RepoContentEntry[]>(
            `repo/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
                repo
            )}/contents?path=${encodeURIComponent(path)}`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Fetches the decoded text content of a file in a repository.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param path - The file path relative to the repository root
     * @param options - Optional request options (e.g., `githubToken` for user-scoped calls)
     * @returns ApiResponse with the decoded UTF-8 content of the file
     */
    getFileContent(
        owner: string,
        repo: string,
        path: string,
        options?: GitHubClientRequestOptions
    ): Promise<ApiResponse<string>> {
        return this.api<string>(
            `repo/repos/${encodeURIComponent(owner)}/${encodeURIComponent(
                repo
            )}/file?path=${encodeURIComponent(path)}`,
            { method: "GET", headers: this.buildGitHubHeaders(options) }
        );
    }

    /**
     * Fetches the git tree for a given tree SHA or branch name.
     *
     * @param owner - The GitHub org/user that owns the repo (e.g., "Banno")
     * @param repo - The repository name (e.g., "backstage")
     * @param treeSha - The tree SHA or branch name (e.g., "main")
     * @param options - Optional request options; set `recursive: true` to fetch the full tree
     * @returns ApiResponse with an array of git tree entries
     */
    getTree(
        owner: string,
        repo: string,
        treeSha: string,
        options?: GitHubGetTreeOptions
    ): Promise<ApiResponse<RepoTreeEntry[]>> {
        const base = `repo/repos/${encodeURIComponent(
            owner
        )}/${encodeURIComponent(repo)}/tree/${encodeURIComponent(treeSha)}`;
        const path = options?.recursive ? `${base}?recursive=true` : base;
        return this.api<RepoTreeEntry[]>(path, {
            method: "GET",
            headers: this.buildGitHubHeaders(options),
        });
    }
}
